<template>
  <div v-if="post" class="post-detail-wrapper">
    <div class="top-wrapper-flex">
      <!-- メイン画像 -->
      <div class="post-image">
        <img :src="post.thumbnail_image || defaultImagePath" @error="onImageError($event, defaultImagePath)"
          alt="投稿のサムネイル画像" />
      </div>
      <div class="post-title-sub-flex">
        <!-- 投稿情報（作成日・ユーザー名・いいね） -->
        <div class="post-sub-wrapper">
          <p class="post-date">{{ post.date_created }}</p>
          <p class="post-user">{{ post.create_user_name }}</p>
          <div class="post-likes-wrapper">
            <button @click="likePost(post.post_id)" :disabled="post.liked" class="post-likes-button">
              <div :class="['post-likes-icon', { 'svg-filter-liked': post.liked, animating: post.animating }]"></div>
            </button>
            <span>{{ post.likes }}</span>
          </div>
        </div>
        <!-- タイトル -->
        <div class="post-title">
          <h2>{{ post.title }}</h2>
        </div>
      </div>
    </div>
    <!-- 本文 -->
    <div class="post-text-wrapper">
      <p class="post-text">
        <span v-html=formatInterests(post.text)></span>
      </p>
    </div>
    <!-- サブ画像 -->
    <div v-for="(image, index) in post.images" :key="index" class="post-sub-image">
      <img v-if="image" :src="image || defaultImagePath" @error="onImageError($event, defaultImagePath)"
        alt="投稿のサブ画像" />
    </div>
    <!-- 戻るボタン -->
    <div class="back-page">
      <a href="" onclick="window.history.back(); return false;">前のページに戻る</a>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";

export default {
  props: {
    post_id: {
      type: Number,
      required: true,
    },
  },
  methods: {
    // 改行を <br> に変換
    formatInterests(text) {
      return text ? text.replace(/\n/g, "<br>") : "";
    },
  },
  setup(props) {
    const post = ref({});
    const defaultImagePath = require('@/assets/img/common/SABA.png');

    // AppSyncやWebSocketの設定
    const apiUrlAppSync = process.env.VUE_APP_APPSYNC_ENDPOINT;
    const authToken = process.env.VUE_APP_APPSYNC_AUTH_TOKEN;
    const appsyncRealtimeEndpoint = process.env.VUE_APP_REALTTIME_ENDPOINT;
    const appsyncHost = process.env.VUE_APP_HOST_APPSYNC;
    const appsyncSubscID = `detail_${props.post_id}`; // 投稿IDごとに一意のIDを設定,;

    // GraphQLクエリ・ミューテーション・サブスクリプション
    const getPostQuery = `
      query getPost($post: String!, $post_id: Int!) {
        getPost(post: $post, post_id: $post_id) {
          post_id
          title
          text
          thumbnail_image
          images
          date_created
          create_user_name
          likes
        }
      }
    `;

    const incrementPostLikesMutation = `
      mutation incrementPostLikes($post: String!, $post_id: Int!) {
        incrementPostLikes(post: $post, post_id: $post_id) {
          post
          post_id
          likes
        }
      }
    `;

    const onPostLikedSubscription = `
      subscription OnPostLikedCount($post: String!, $post_id: Int!) {
        onPostLikedCount(post: $post, post_id: $post_id) {
          post
          post_id
          likes
        }
      }
    `;

    // 投稿データを取得する関数
    const fetchPost = async () => {
      try {
        const response = await axios.post(
          apiUrlAppSync,
          {
            query: getPostQuery,
            variables: { post: "post", post_id: props.post_id },
          },
          {
            headers: {
              Authorization: authToken,
            },
          }
        );
        // 投稿データをセット
        post.value = {
          ...response.data.data.getPost,
          liked: false,
          animating: false,
        };
      } catch (error) {
        console.error("Error fetching post:", error);
      }
    };

    // いいねボタンを押す関数
    const likePost = async () => {
      try {
        // UI更新（リアクティブ）
        post.value.liked = true;
        post.value.animating = true;

        const response = await axios.post(
          apiUrlAppSync,
          {
            query: incrementPostLikesMutation,
            variables: { post: "post", post_id: props.post_id },
          },
          {
            headers: {
              Authorization: authToken,
            },
          }
        );

        const updatedPost = response.data.data.incrementPostLikes;
        if (updatedPost) {
          post.value.likes = updatedPost.likes; // サーバーからの最新いいね数をセット
        }

        // アニメーション終了
        setTimeout(() => {
          post.value.animating = false;
        }, 300);
      } catch (error) {
        console.error("Error liking post:", error);
      }
    };

    // サブスクリプションを設定する関数
    const subscribeToLikes = () => {
      // WebSocketのヘッダー
      const header = { host: appsyncHost, Authorization: authToken };
      const url =
        `${appsyncRealtimeEndpoint}?header=` +
        btoa(JSON.stringify(header)) +
        "&payload=" +
        btoa(JSON.stringify({}));

      const socket = new WebSocket(url, ["graphql-ws"]);

      // WebSocketが開いたときの処理
      socket.addEventListener("open", () => {
        console.log("WebSocket connected.");
        socket.send(JSON.stringify({ type: "connection_init" })); // 初期化メッセージ送信
      });

      // サブスクリプションメッセージを送信
      socket.addEventListener("message", (event) => {

        const eventData = JSON.parse(event.data);

        if (eventData.type === "connection_ack") {
          console.log("Subscription connection acknowledged.");
          socket.send(getSubscRegistMessage(appsyncSubscID));
        } else if (eventData.type === "start_ack") {
          console.log("Subscription start acknowledged");
        } else if (eventData.type === "data") {
          console.log("Subscription data received:", eventData);
          const updatedPost = eventData.payload.data.onPostLikedCount;
          if (updatedPost && updatedPost.post_id === props.post_id) {
            post.value.likes = updatedPost.likes; // いいね数をリアルタイムに更新
          }
        } else if (eventData.type === "ka") {
          console.log("Keep-alive signal received.");
        } else if (eventData.type === "error") {
          console.error("Subscription error:", eventData);
        } else {
          console.warn("Unhandled message type:", eventData.type);
        }
      });

      // WebSocketを閉じる処理
      const closeSocket = () => {
        socket.send(JSON.stringify({ type: "stop", id: appsyncSubscID })); // サブスクリプション停止
        socket.close();
        console.log("WebSocket connection closed.");
      };

      // コンポーネントが破棄される前にWebSocketを閉じる
      onBeforeUnmount(() => {
        closeSocket();
      });

      // サブスクリプション登録メッセージを作成
      function getSubscRegistMessage(appsyncSubscID) {
        return JSON.stringify({
          id: appsyncSubscID,
          payload: {
            data: JSON.stringify({ query: onPostLikedSubscription, variables: { post: "post", post_id: props.post_id } }),
            extensions: {
              authorization: {
                Authorization: authToken,
                host: appsyncHost,
              },
            },
          },
          type: "start",
        });
      }
    };

    // 初期化処理
    onMounted(() => {
      fetchPost(); // 投稿データ取得
      subscribeToLikes(); // サブスクリプション設定
    });

    // 画像エラー時の処理
    const onImageError = (event, defaultPath) => {
      event.target.src = defaultPath;
    };

    return {
      post,
      likePost,
      defaultImagePath,
      onImageError,
    };
  },
  mounted(){
    window.scrollTo(0, 0);
  }
};
</script>


<style scoped>
.post-detail-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 80px;
  margin-top: 250px;
  margin-bottom: 100px;
}

.top-wrapper-flex {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  margin-left: 10%;
  margin-right: 10%;
  width: 80%;
}

/*************** トプ画 ***************/

.post-image {
  width: 60%;
  min-height: 600px;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.post-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}


/*************** タイトル ***************/

.post-title-sub-flex {
  display: flex;
  flex-direction: row;
  justify-content:space-between;
  width: 20%;
  height: auto;
  min-height: 600px;
}

.post-title {
  width: auto;
  height: auto;
  font-size: 20px;
  font-weight: normal;
  -webkit-writing-mode: vertical-rl;
  writing-mode: vertical-rl;
  font-family: "ShipporiMinchoBold";
  line-height: 2.0;
  color: #493c33;
  z-index: 3;
}


/*************** 作成日と作成者情報といいね ***************/

.post-sub-wrapper {
  width: auto;
  height: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: sans-serif;
  font-size: 14px;
  -webkit-writing-mode: vertical-rl;
  writing-mode: vertical-rl;
  color: #86827e;
}

.post-date,
.post-user {
  display: inline-block;
  margin-bottom: 17px;
}

.post-likes-wrapper {
  display: flex;
  align-items: center;
  text-align: right;
  font-size: 14px;
  z-index: 4;
  padding: 50px;
  -webkit-tap-highlight-color: transparent;
  /* スマホでのタップ時のハイライトを無効化 */
}

.post-likes-wrapper button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.post-likes-wrapper span {
  font-size: 12px;
  color: #696867;
}

.post-likes-icon {
  width: 50px;
  height: 50px;
  cursor: pointer;
  background: transparent url(@/assets/img/history/likes.svg) no-repeat top center /cover;
  filter: brightness(0) saturate(100%) invert(52%) sepia(10%) saturate(167%) hue-rotate(349deg) brightness(97%) contrast(84%);
}

.post-likes-icon:hover {
  filter: brightness(0) saturate(100%) invert(15%) sepia(87%) saturate(4756%) hue-rotate(335deg) brightness(80%) contrast(109%);
}

.svg-filter-liked {
  filter: brightness(0) saturate(100%) invert(15%) sepia(87%) saturate(4756%) hue-rotate(335deg) brightness(80%) contrast(109%);
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}

.post-likes-icon.animating {
  animation: pulse 0.3s ease-in-out;
}

.post-likes-wrapper span {
  font-size: 10px;
  color: #696867;
  -webkit-writing-mode: horizontal-tb;
  writing-mode: horizontal-tb;
}


/*************** 本文 ***************/

.post-text-wrapper {
  margin-left: 10%;
  margin-right: 10%;
  margin-bottom: 100px;
  width: 80%;
  font-family: "ShipporiMincho";
  color: #493c33;
  line-height: 2;
}

.post-text span {
  margin-bottom: 10px;
}


/*************** サブ画像 ***************/

.post-sub-image {
  margin-left: 10%;
  margin-right: 10%;
  margin-bottom: 200px;
  width: 70%;
  height: auto;
  max-width: 800px;
  display: flex;
  align-self: center;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 80px;
}

.post-sub-image img {
  max-width: 100%;
  width: 100%;
  max-height: 100%;
  object-fit: cover;
}


/*************** 戻る ***************/

.back-page {
  position: absolute;
  left: 10%;
  bottom: 0px;
  width: 200px;
  height: 30px;
}

.back-page a {
  font-size: 13px;
  font-family: sans-serif;
  color: #696867;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  /* スマホでのタップ時のハイライトを無効化 */
}

.back-page a:hover {
  color: #69b1a9;
}



/* タブレット、スマートフォン向けのスタイル */
@media screen and (max-width: 570px) {
  .post-detail-wrapper {
  row-gap: 80px;
  margin-top: 100px;
  margin-bottom: 100px;
}

.top-wrapper-flex {
  flex-direction: column-reverse;
  justify-content: center;
  margin-left: 5%;
  margin-right: 5%;
  width: 90%;
}

.post-image {
  width: 60%;
  height: auto;
  min-height: unset;
}

.post-title-sub-flex {
  flex-direction: column-reverse;
  justify-content:center;
  width: 100%;
  height: auto;
  min-height: unset;
}

  .post-title {
    font-size: 12px;
    -webkit-writing-mode: horizontal-tb;
    writing-mode: horizontal-tb;
    white-space:wrap;
    text-align: center;
  }

  .post-sub-wrapper {
    font-size: 10px;
    -webkit-writing-mode: horizontal-tb;
    writing-mode: horizontal-tb;
    white-space: nowrap;
    margin-bottom: 50px;
  }

  .post-date,
  .post-user {
    margin-bottom: 0px;
    margin-right: 10px;
  }

  .post-likes-wrapper {
    transform: translateY(5px);
    padding: 10px;
  }

  .post-likes-icon {
    width: 35px;
    height: 35px;
  }

  .post-likes-wrapper span {
    font-size: 8px;
  }

  .post-text-wrapper {
    margin-top: 20px;
    margin-bottom: 40px;
    margin-left: 10%;
    width: 80%;
    font-size: 13px;
    line-height: 2;
  }

  .post-sub-image {
    margin-left: 10%;
    margin-bottom: 10px;
    width: 80%;
    row-gap: 0px;
  }

  .back-page {
    position: relative;
    margin-bottom: 30px;
    width: 200px;
    height: 0px;
  }

  .back-page a {
    font-size: 11px;
  }

}
</style>